import { useState } from 'react';
import { authApi, ApiError, SignupData } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';
import {
  USERNAME_MAX_LENGTH,
  USERNAME_RULES_HINT,
  sanitizeUsername,
  validateUsername,
} from '@/utils/username';

interface SignupFormProps {
  onSwitchToLogin: () => void;
  onSignupSuccess: (username: string) => void;
  onSwitchToConfirm: () => void;
}

const validatePassword = (password: string): string | null => {
  const regex = /^\S+.*\S+$/;
  const minLength = 8;
  const hasUpperCase = /[A-Z]/;
  const hasLowerCase = /[a-z]/;
  const hasNumber = /[0-9]/;
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/;

  if (!regex.test(password)) {
    return 'Password must not start or end with whitespace';
  }
  if (password.length < minLength) {
    return `Password must be at least ${minLength} characters long`;
  }
  if (!hasUpperCase.test(password)) {
    return 'Password must contain at least one uppercase letter';
  }
  if (!hasLowerCase.test(password)) {
    return 'Password must contain at least one lowercase letter';
  }
  if (!hasNumber.test(password)) {
    return 'Password must contain at least one number';
  }
  if (!hasSpecialChar.test(password)) {
    return 'Password must contain at least one special character';
  }
  return null;
};

export const SignupForm = ({ onSwitchToLogin, onSignupSuccess, onSwitchToConfirm }: SignupFormProps) => {
  const [signupData, setSignupData] = useState<SignupData>({
    username: '',
    email: '',
    password: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [usernameNotice, setUsernameNotice] = useState<string>('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'username') {
      const sanitized = sanitizeUsername(value);
      // Tell the user why what they typed did not land in the field
      setUsernameNotice(
        sanitized === value
          ? ''
          : value.includes('@')
            ? 'Usernames cannot be an email address'
            : 'Removed characters that are not allowed in a username'
      );
      setSignupData((prev) => ({ ...prev, username: sanitized }));
      setError('');
      return;
    }

    setSignupData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!signupData.username || !signupData.email || !signupData.password) {
      setError('Please fill out all fields');
      return;
    }

    const usernameError = validateUsername(signupData.username);
    if (usernameError) {
      setError(usernameError);
      return;
    }

    const passwordError = validatePassword(signupData.password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setIsLoading(true);

    try {
      const response = await authApi.signup(signupData);
      setSuccess(true);
    } catch (err) {
      const error = err as ApiError;

      let message = 'Signup failed. Please try again';
      if (error.error) {
        if (typeof error.error === 'string') {
          // Handle when error.error is a string (most common case from backend)
          message = error.error;
        } else if (Array.isArray(error.error) && error.error[0]?.error) {
          message = error.error[0].error;
        } else if (typeof error.error === 'object' && error.error !== null && 'message' in error.error) {
          message = error.error.message as string;
        }
      } else if (error.message) {
        message = error.message;
      }

      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueToConfirmation = () => {
    onSignupSuccess(signupData.username);
  };

  if (success) {
    return (
      <div className="w-full max-w-md mx-auto animate-slide-up">
        <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 hover:shadow-3xl hover:border-white/30 transition-all duration-300 relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 via-blue-500/20 to-purple-500/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"></div>

          <div className="text-center mb-6 relative z-10">
            <CheckCircle2 className="w-16 h-16 text-green-400 mx-auto mb-4 animate-bounce" />
            <h2 className="text-3xl font-bold text-white mb-2 bg-gradient-to-r from-white to-green-200 bg-clip-text text-transparent">Account Created!</h2>
          </div>

          <div className="bg-green-500/20 border border-green-400/30 rounded-xl p-6 mb-6">
            <p className="text-white text-center mb-4">
              We've sent a confirmation code to:
            </p>
            <p className="text-white font-semibold text-center mb-4">
              {signupData.email}
            </p>
            <p className="text-white/80 text-sm text-center">
              Check your email (including spam folder) and enter the confirmation code in the next step.
            </p>
          </div>

          <Button
            onClick={handleContinueToConfirmation}
            className="w-full bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 hover:from-green-700 hover:via-blue-700 hover:to-purple-700 text-white font-semibold py-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300 relative overflow-hidden group"
          >
            <span className="relative z-10">Enter Confirmation Code</span>
            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
          </Button>

          <div className="mt-4 text-center">
            <p className="text-white/60 text-xs">
              Didn't receive the email? It may take a few minutes to arrive.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto animate-slide-up">
      <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-8 hover:shadow-3xl hover:border-white/30 transition-all duration-300 relative overflow-hidden group">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"></div>
        <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>

        <div className="text-center mb-6 relative z-10">
          <h2 className="text-3xl font-bold text-white mb-2 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">Create Account</h2>
          <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 mt-4 border border-white/30">
            <p className="text-white font-semibold">
              Your AI Patent Analysis Awaits
            </p>
            <p className="text-white font-bold text-lg mt-1 drop-shadow-md">
              Get 50 Credits Free!
            </p>
            <p className="text-white/90 text-sm mt-1">No Credit Card Required</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-2 text-white text-sm animate-shake backdrop-blur-sm relative z-10 font-medium">
            <AlertCircle className="w-4 h-4 flex-shrink-0 animate-pulse" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSignup} className="space-y-4 relative z-10">
          <div className="transform transition-all duration-300 hover:scale-[1.02]">
            <Input
              type="text"
              name="username"
              placeholder="Username"
              value={signupData.username}
              onChange={handleChange}
              maxLength={USERNAME_MAX_LENGTH}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="bg-white/90 border-white/30 text-gray-900 placeholder:text-gray-500 focus:bg-white focus:border-purple-400 focus:ring-2 focus:ring-purple-400/50 transition-all duration-200"
            />
            <p className={`mt-1 text-xs pl-1 ${usernameNotice ? 'text-amber-300' : 'text-white/60'}`}>
              {usernameNotice || USERNAME_RULES_HINT}
            </p>
          </div>

          <div className="transform transition-all duration-300 hover:scale-[1.02]">
            <Input
              type="email"
              name="email"
              placeholder="Email Address"
              value={signupData.email}
              onChange={handleChange}
              className="bg-white/90 border-white/30 text-gray-900 placeholder:text-gray-500 focus:bg-white focus:border-purple-400 focus:ring-2 focus:ring-purple-400/50 transition-all duration-200"
            />
          </div>

          <div className="transform transition-all duration-300 hover:scale-[1.02]">
            <Input
              type="password"
              name="password"
              placeholder="Password"
              value={signupData.password}
              onChange={handleChange}
              className="bg-white/90 border-white/30 text-gray-900 placeholder:text-gray-500 focus:bg-white focus:border-purple-400 focus:ring-2 focus:ring-purple-400/50 transition-all duration-200"
            />
          </div>

          <div className="bg-white/20 border border-white/30 rounded-lg p-3 text-xs text-gray-900 backdrop-blur-sm">
            Password must contain: 8+ chars, 1 uppercase, 1 lowercase, 1 number, 1 special char
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-purple-600 via-blue-600 to-pink-600 hover:from-purple-700 hover:via-blue-700 hover:to-pink-700 text-white font-semibold py-6 rounded-xl shadow-lg hover:shadow-2xl transform hover:scale-[1.02] transition-all duration-300 relative overflow-hidden group"
          >
            <span className="relative z-10">
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin inline" />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
          </Button>
        </form>

        <div className="mt-6 flex flex-col gap-3 text-center relative z-10">
          <button
            onClick={onSwitchToConfirm}
            className="w-full bg-white/10 hover:bg-white/20 border-2 border-white/30 hover:border-white/50 text-white font-semibold py-3 px-6 rounded-xl shadow-md hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300"
          >
            Enter Confirmation Code
          </button>
          <button
            onClick={onSwitchToLogin}
            className="w-full bg-white/10 hover:bg-white/20 border-2 border-white/30 hover:border-white/50 text-white font-semibold py-3 px-6 rounded-xl shadow-md hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2 group"
          >
            <span>Already have an account? Log in</span>
            <span className="inline-block transform group-hover:translate-x-1 transition-transform duration-200">→</span>
          </button>
        </div>
      </div>
    </div>
  );
};
